/**
 * PATH: packages/email-hub/src/feature/hooks/useEmails.listCache.ts
 * FILE_ID: EMAILHUB-FEATURE-USE-EMAILS-LISTCACHE-001
 * PURPOSE: SWR-style persistence of the DEFAULT inbox list page so the
 *          inbox paints INSTANTLY from the last-seen rows on mount/reload
 *          instead of showing a cold centred spinner until the network
 *          fetch returns. Revalidation (the normal two-phase fetch) then
 *          overwrites the seed in place.
 * WHY: Operator 2026-05-31: "seeing the list of emails is taking time".
 *      ``useEmails`` started with ``emails=[]`` + ``loadingInitial=true``,
 *      so EVERY inbox mount blocked on a network round-trip before showing
 *      anything. Seeding from a persisted snapshot removes that wait for
 *      the common (unfiltered) inbox view.
 * SAFETY:
 *   - localStorage (durable, shared across tabs) with a sessionStorage
 *     fallback. Promoted from per-tab sessionStorage (MR-C offline
 *     resilience, 2026-07-06): reloading OFFLINE in a NEW tab used to
 *     paint zero emails + an error banner because the seed never left
 *     the original tab. Cross-user safety does NOT rest on tab isolation:
 *     keys are scoped by the current user id, and the identity-drift /
 *     logout reset (``resetEmailListCache`` →
 *     ``clearPersistedInboxLists``) wipes every snapshot in BOTH stores.
 *   - Keyed by the current user id (caller-supplied) so a re-login on the
 *     SAME tab can't paint the previous user's rows.
 *   - Best-effort: any storage/parse failure degrades to "no seed" (cold
 *     fetch), never throws.
 *   - Only the DEFAULT view is persisted (caller gates on an unfiltered
 *     query) so a filtered/search result never seeds the plain inbox.
 *   - Staleness: fresh-window cap while online (better a brief spinner
 *     than day-old rows when a revalidate is about to land), but while
 *     OFFLINE an older snapshot (up to the offline cap) still paints —
 *     stale-but-visible always beats empty-with-banner, because no
 *     revalidate is coming to rescue the empty state.
 */
import type { Email } from './useEmails.types'

const KEY_PREFIX = 'qf:inbox-list:'
// Cap the snapshot — first paint only needs the visible head of the list,
// and web-storage quotas are small. ~60 rows covers a tall viewport.
const MAX_PERSISTED_ROWS = 60
// Bump if the persisted shape changes incompatibly.
const SNAPSHOT_VERSION = 1
// Don't paint a stale snapshot older than this WHILE ONLINE — better a
// brief spinner than rows from days ago. Revalidation still fires
// regardless.
const MAX_AGE_MS = 24 * 60 * 60 * 1000
// While OFFLINE, accept a much older snapshot — the alternative is an
// empty inbox with an error banner that nothing will ever revalidate.
const MAX_OFFLINE_AGE_MS = 7 * 24 * 60 * 60 * 1000

interface Snapshot {
  v: number
  at: number
  rows: Email[]
}

function storage(): Storage | null {
  // localStorage first (survives tab close + is visible to new tabs);
  // sessionStorage fallback for privacy modes that block localStorage.
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage
    }
  } catch {
    // fall through
  }
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null
    return window.sessionStorage
  } catch {
    return null
  }
}

function keyFor(cacheKey: string): string {
  return `${KEY_PREFIX}${cacheKey}`
}

/** Write the default-inbox snapshot. No-op on any failure (quota, etc.). */
export function persistInboxList(cacheKey: string, rows: Email[]): void {
  const s = storage()
  if (!s || !cacheKey || !Array.isArray(rows)) return
  try {
    const snapshot: Snapshot = {
      v: SNAPSHOT_VERSION,
      at: Date.now(),
      rows: rows.slice(0, MAX_PERSISTED_ROWS),
    }
    s.setItem(keyFor(cacheKey), JSON.stringify(snapshot))
  } catch {
    // Quota / serialization failure — drop the snapshot silently. The
    // inbox just falls back to the cold spinner on the next mount.
  }
}

/** Read the default-inbox snapshot, or null if absent / stale / corrupt.
 *  ``online`` is injectable for tests; defaults to ``navigator.onLine``
 *  so an offline reload accepts an older (but still bounded) snapshot. */
export function readInboxList(
  cacheKey: string,
  online: boolean = typeof navigator === 'undefined' ? true : navigator.onLine !== false,
): Email[] | null {
  const s = storage()
  if (!s || !cacheKey) return null
  try {
    const raw = s.getItem(keyFor(cacheKey))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Snapshot
    const maxAge = online ? MAX_AGE_MS : MAX_OFFLINE_AGE_MS
    if (
      !parsed ||
      parsed.v !== SNAPSHOT_VERSION ||
      !Array.isArray(parsed.rows) ||
      typeof parsed.at !== 'number' ||
      Date.now() - parsed.at > maxAge
    ) {
      return null
    }
    // Defensive: only rows with a string id are renderable.
    const rows = parsed.rows.filter(
      (r): r is Email => !!r && typeof (r as Email).id === 'string',
    )
    return rows.length > 0 ? rows : null
  } catch {
    return null
  }
}

/** Drop every persisted inbox snapshot. Wired into ``resetEmailListCache``
 *  so an identity change / logout never re-seeds the previous session.
 *  Sweeps BOTH stores — localStorage (current) and sessionStorage (both
 *  the pre-promotion location and the privacy-mode fallback). */
export function clearPersistedInboxLists(): void {
  const stores: Array<Storage | null> = []
  try {
    stores.push(typeof window !== 'undefined' ? window.localStorage : null)
  } catch {
    stores.push(null)
  }
  try {
    stores.push(typeof window !== 'undefined' ? window.sessionStorage : null)
  } catch {
    stores.push(null)
  }
  for (const s of stores) {
    if (!s) continue
    try {
      const toRemove: string[] = []
      for (let i = 0; i < s.length; i += 1) {
        const k = s.key(i)
        if (k && k.startsWith(KEY_PREFIX)) toRemove.push(k)
      }
      for (const k of toRemove) s.removeItem(k)
    } catch {
      // ignore
    }
  }
}
